/**
 * Currency symbols and the picker's option list, in one place.
 *
 * The budget spans NGN, CNY, USD, GBP and EUR, and the symbol lookup had been
 * written inline on the one page that needed it. A second page needing the
 * same map is how a lookup drifts — one copy learns EUR, the other doesn't,
 * and a euro amount renders with a dollar sign on exactly one screen.
 */

import { getAppLocale } from './locale';
import { FIXED_RATES_AS_OF, NGN_PER_USD, USD_TO_CNY, USD_TO_EUR, USD_TO_GBP } from '../constants/exchangeRates';

const SYMBOLS: Record<string, string> = {
  NGN: '₦',
  CNY: '¥',
  GBP: '£',
  EUR: '€',
  USD: '$',
};

/** Currencies the pickers offer. */
export type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'CNY' | 'NGN';

/** A project's primary currency; '' means the project has not chosen one. */
export type ProjectCurrency = CurrencyCode | '';

/** What every amount shows when there is no currency to format it with. */
export const CURRENCY_NOT_SET = 'currency not set';

/** True when `code` is a usable currency code (not missing, empty or blank). */
export function isCurrencySet(code?: string | null): code is string {
  return typeof code === 'string' && code.trim() !== '';
}

/**
 * The symbol for an ISO code. There is deliberately no fallback currency:
 * a missing code gives '' (never a symbol), and a code we have no symbol for
 * is shown as the code itself, so an amount is never labelled with the wrong
 * currency.
 */
export function currencySymbol(code?: string | null): string {
  if (!isCurrencySet(code)) return '';
  const upper = code.trim().toUpperCase();
  return SYMBOLS[upper] ?? upper;
}

/** Options for a currency `SearchableSelect`, symbol shown beside the code. */
export const CURRENCY_OPTIONS = Object.entries(SYMBOLS).map(([value, symbol]) => ({
  value,
  label: `${symbol} ${value}`,
}));

function groupedWhole(amount: number): string {
  return Math.round(amount).toLocaleString(getAppLocale());
}

/** Amount with its symbol, thousands-separated, no decimals. When the
 *  currency is not set it returns "currency not set" rather than a symbol. */
export function formatMoney(amount: number, code?: string | null): string {
  if (!isCurrencySet(code)) return CURRENCY_NOT_SET;
  return `${currencySymbol(code)}${groupedWhole(amount)}`;
}

/** A per-unit amount in its own currency with cents kept when present,
 *  e.g. "¥13.80" or "₦85,000". Unlike formatMoney it does not round,
 *  because a unit cost of 13.80 must not read as 14. */
export function formatUnitCost(amount: number, code?: string | null): string {
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${currencySymbol(code)}${formatted}`;
}

/** Compact form for tight spaces (stat cards) where a full thousands-
 *  separated figure gets clipped by the card's fixed width. Falls back to
 *  formatMoney below 1M, where there's nothing to gain by compacting. */
export function formatCompactMoney(amount: number, code?: string | null): string {
  if (!isCurrencySet(code)) return CURRENCY_NOT_SET;
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) {
    const sign = amount < 0 ? '-' : '';
    return `${sign}${currencySymbol(code)}${(abs / 1_000_000).toFixed(3)}M`;
  }
  return formatMoney(amount, code);
}

/** A project's money formatters, bound to its currency (or to none). */
export interface Money {
  /** The project currency code, '' when not set. */
  currency: string;
  isSet: boolean;
  symbol: string;
  /** For field labels: the currency code, or "currency not set". */
  unit: string;
  /** `code` is a row's own currency; it wins over the project currency. */
  format: (amount: number, code?: string | null) => string;
  formatCompact: (amount: number, code?: string | null) => string;
}

export function makeMoney(projectCurrency?: string | null): Money {
  const currency = isCurrencySet(projectCurrency) ? projectCurrency.trim().toUpperCase() : '';
  const pick = (code?: string | null) => (isCurrencySet(code) ? code : currency);
  return {
    currency,
    isSet: currency !== '',
    symbol: currencySymbol(currency),
    unit: currency || CURRENCY_NOT_SET,
    format: (amount, code) => formatMoney(Number(amount) || 0, pick(code)),
    formatCompact: (amount, code) => {
      const n = Number(amount) || 0;
      const c = pick(code);
      if (!isCurrencySet(c)) return CURRENCY_NOT_SET;
      const abs = Math.abs(n);
      const sign = n < 0 ? '-' : '';
      const sym = currencySymbol(c);
      const trim = (v: number) => v.toFixed(1).replace(/\.0$/, '');
      if (abs >= 1_000_000_000) return `${sign}${sym}${trim(abs / 1_000_000_000)}B`;
      if (abs >= 1_000_000) return `${sign}${sym}${trim(abs / 1_000_000)}M`;
      if (abs >= 1_000) return `${sign}${sym}${trim(abs / 1_000)}K`;
      return `${sign}${sym}${groupedWhole(abs)}`;
    },
  };
}

export interface ConvertibleRow {
  amount: number;
  currency?: string | null;
  /** Project-currency units per 1 unit of `currency`; null/0 = no rate yet. */
  fxRate?: number | null;
}

export interface ConvertibleSum<T extends ConvertibleRow> {
  total: number;
  /** Rows left out of `total` because they have no usable rate. */
  unconverted: T[];
}

/**
 * Totals in the project currency. A row in another currency with no exchange
 * rate is never added raw: it is returned in `unconverted` so the screen can
 * mark it and say it was left out.
 */
export function sumConvertible<T extends ConvertibleRow>(
  rows: T[],
  projectCurrency?: string | null
): ConvertibleSum<T> {
  const project = isCurrencySet(projectCurrency) ? projectCurrency.trim().toUpperCase() : '';
  let total = 0;
  const unconverted: T[] = [];
  for (const row of rows) {
    const code = isCurrencySet(row.currency) ? row.currency.trim().toUpperCase() : '';
    if (project && code === project) {
      total += row.amount;
    } else if (project && Number.isFinite(row.fxRate) && (row.fxRate as number) > 0) {
      total += row.amount * (row.fxRate as number);
    } else {
      unconverted.push(row);
    }
  }
  return { total, unconverted };
}

const RATE_PER_USD: Record<string, number> = {
  USD: 1,
  NGN: NGN_PER_USD,
  CNY: USD_TO_CNY,
  EUR: USD_TO_EUR,
  GBP: USD_TO_GBP,
};

/** Units of `code` per 1 USD at the app's FIXED rates, or null for a currency
 *  that is not in the table. */
export function fixedRatePerUsd(code: string): number | null {
  const rate = RATE_PER_USD[code.trim().toUpperCase()];
  return rate ? rate : null;
}

/** Converts an amount between any two of the app's currencies via a USD
 *  pivot — was written inline in BudgetCalculator.tsx and would have been
 *  duplicated a third time for bundle actual-cost-vs-target comparisons.
 *  Unknown currency codes pass through unconverted. */
export function convertCurrency(amount: number, from: string, to: string): number {
  if (from === to) return amount;
  const fromRate = RATE_PER_USD[from];
  const toRate = RATE_PER_USD[to];
  if (!fromRate || !toRate) return amount;
  return (amount / fromRate) * toRate;
}

/** The currency the API itself quotes amounts in (shipping rate cards, the
 *  `*_ngn` columns, `budget_items.fx_rate`). It is a property of the backend
 *  contract, named here once; no screen may assume it is the project currency.
 *  Renaming the columns needs an ans-backend change. */
export const API_BASE_CURRENCY = 'NGN';

/** An amount the API quotes in {@link API_BASE_CURRENCY}, shown in the project
 *  currency. With no project currency it says so rather than guessing. */
export function formatApiAmount(amount: number, projectCurrency?: string | null): string {
  if (!isCurrencySet(projectCurrency)) return CURRENCY_NOT_SET;
  return formatMoney(convertCurrency(amount, API_BASE_CURRENCY, projectCurrency.trim().toUpperCase()), projectCurrency);
}

/** Date label of the fixed rate table behind `convertCurrency`. */
export const FIXED_RATES_DATE_LABEL = FIXED_RATES_AS_OF;

/** Naira value of an amount at the app's FIXED rates (not live), or null when
 *  the currency is not in the table. Unlike `convertCurrency`, an unknown
 *  code is reported rather than passed through unconverted. */
export function fixedRateToNgn(amount: number, code: string): number | null {
  const upper = code.toUpperCase();
  if (upper === API_BASE_CURRENCY) return amount;
  if (!RATE_PER_USD[upper]) return null;
  return convertCurrency(amount, upper, API_BASE_CURRENCY);
}

/**
 * Converts a budget_items-shaped amount to the API base currency using the
 * row's own locked `fx_rate` (base units per 1 unit of `currency`, snapshotted
 * on the row when the cost was recorded; distinct from the live app-wide
 * picker rates in `exchangeRates.ts` used by `convertCurrency` above). A row
 * already in the base currency converts to itself. A row in another currency
 * with no `fx_rate` yet (an un-migrated legacy row) returns `null` rather than
 * quietly treating the raw foreign number as base currency: GitHub issue #97
 * was a $35 charge rendering as if it were 35 of the base currency.
 */
export function budgetAmountToBase(
  amount: number,
  currency?: string | null,
  fxRate?: number | string | null
): number | null {
  const code = (currency || API_BASE_CURRENCY).toUpperCase();
  if (code === API_BASE_CURRENCY) return amount;
  const rate = typeof fxRate === 'string' ? parseFloat(fxRate) : fxRate;
  if (rate == null || !Number.isFinite(rate) || rate <= 0) return null;
  return amount * rate;
}

/**
 * Large money, abbreviated for a stat card. A full ₦23,431,356 overflows the
 * tile it sits in, and at that magnitude the trailing digits are noise — the
 * figure is read for its scale. Below a million it stays exact, where the
 * digits still carry meaning.
 */
export function compactMoney(amount: number, code?: string | null): string {
  if (!isCurrencySet(code)) return CURRENCY_NOT_SET;
  const symbol = currencySymbol(code);
  const abs = Math.abs(amount);
  const unit = abs >= 1e9 ? { d: 1e9, s: 'B' } : abs >= 1e6 ? { d: 1e6, s: 'M' } : null;
  if (!unit) return `${symbol}${groupedWhole(amount)}`;
  const scaled = amount / unit.d;
  // Trailing zeros trimmed: "$23.4M", not "$23.400M".
  return `${symbol}${parseFloat(scaled.toFixed(3))}${unit.s}`;
}
